import { Component, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AlertTriangle, ExternalLink, FileArchive } from "lucide-react";

const EXPORT_INFO =
  "Alle Daten standen zum 01.04.2026 von ungefähr 9:00 Uhr (MESZ) und wurden zu diesem Zeitpunkt exportiert.";

/** Öffentlicher Ordner-Link auf Proton Drive (ZIP/CSV liegen nicht auf dieser Website). */
const PROTON_DRIVE_EXPORTS_URL =
  "https://drive.proton.me/urls/BA92Q9Q5P4#bk3GPvk9ph8O";

function OpdashUnavailable() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-4 py-12 md:py-16">
        <div className="mb-8 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-destructive/15">
            <AlertTriangle
              className="h-6 w-6 text-destructive"
              aria-hidden
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
              OPDash ist nicht mehr verfügbar
            </h1>
          </div>
        </div>

        <div className="space-y-4 text-sm leading-relaxed text-foreground/90">
          <p>
            <strong>OPDash wurde am 01.04.2026 vollständig abgeschaltet.</strong>{" "}
            Es gibt keine Anmeldung, kein Dashboard, keine Markt- oder
            Profilseiten und keine weiteren Funktionen mehr. Alle bisherigen
            Unterpfade (z.&nbsp;B. <code className="rounded bg-muted px-1 py-0.5 text-xs">/market</code>,{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/dash</code>) zeigen dieselbe
            Information.
          </p>

          <p>
            Öffentlich nutzbare Rohdaten wurden einmalig exportiert und liegen
            auf <strong>Proton Drive</strong> zum Herunterladen bereit – nicht
            auf dieser Website. <strong>{EXPORT_INFO}</strong>
          </p>

          <p>
            Zuvor angekündigt: Gesammelte Daten können nach dem Shutdown
            serverseitig gelöscht worden sein; die Dateien im verlinkten Ordner
            sind der veröffentlichte Stand zum genannten Zeitpunkt.
          </p>

          <p>
            Der <strong>Discord-Bot</strong> wurde abgeschaltet. Wenn du OPDash
            mit Discord verbunden hattest, prüfe bei Bedarf unter{" "}
            <strong>Discord → Einstellungen → Verbundene Apps</strong>, ob die
            Verbindung entfernt werden soll.
          </p>

          <p className="border-t border-border pt-4 text-xs text-muted-foreground">
            OPDash wurde eingestellt, weil andere Projekte Vorrang haben und der
            Aufwand für die Wartung zu hoch war. Danke für deine Nutzung und
            Unterstützung.
            <br />
            <span className="mt-2 inline-block font-medium text-foreground/80">
              OPDash Founder – Rafael
            </span>
          </p>
        </div>

        <section className="mt-10" aria-labelledby="downloads-heading">
          <h2
            id="downloads-heading"
            className="mb-2 flex items-center gap-2 text-lg font-semibold"
          >
            <FileArchive className="h-5 w-5 text-muted-foreground" aria-hidden />
            Datenexporte (CSV &amp; ZIP)
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Du wirst zu einem öffentlichen Ordner auf Proton Drive weitergeleitet.
            Dort kannst du ZIP und Einzeldateien herunterladen.
          </p>
          <a
            href={PROTON_DRIVE_EXPORTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full max-w-md flex-col gap-1 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 text-left transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex items-center gap-2 font-semibold text-foreground">
              <ExternalLink className="h-5 w-5 shrink-0 text-primary" aria-hidden />
              Zu den Exporten auf Proton Drive
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              Öffnet drive.proton.me in einem neuen Tab
            </span>
          </a>
        </section>
      </div>
    </div>
  );
}

class StaticPageErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-background p-6 text-foreground">
          <h1 className="text-lg font-semibold">OPDash ist nicht mehr verfügbar</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Die Seite konnte nicht geladen werden. Bitte lade die Seite neu oder
            versuche es später erneut.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => (
  <StaticPageErrorBoundary>
    <BrowserRouter>
      <Routes>
        <Route path="*" element={<OpdashUnavailable />} />
      </Routes>
    </BrowserRouter>
  </StaticPageErrorBoundary>
);

export default App;
